import type { ModuleInfo } from '../Bridge';
import { Bridge } from '../Bridge';
import { once } from '../utils';
import type { Version } from '../Version';

export class LaunchScreenModule {
    constructor(
        readonly bridge: Bridge,
        readonly module: ModuleInfo<'launch-screen', 0>,
    ) {}

    private request<T>(data: any): Promise<T> {
        return this.bridge.request(this.module.id, data);
    }

    /**
     * Since launch-screen@0.0.0
     */
    async isShowing() {
        const response: boolean = await this.request({
            type: 'is showing',
        });
        return response;
    }

    /**
     * Since launch-screen@0.0.0
     */
    async show() {
        await this.request({
            type: 'show',
        });
    }

    /**
     * Since launch-screen@0.0.0
     */
    async hide() {
        await this.request({
            type: 'hide',
        });
    }
}

/**
 * The LaunchScreen module provides methods to show and hide the launch screen.
 *
 * Get an instance of this module using `LaunchScreen.getInstance()`.
 */
export class LaunchScreen {
    /**
     * Creates a new instance using the provided bridge.
     */
    static create = (bridge: Bridge) => {
        const module = bridge.findModule('launch-screen', 0);
        if (module) {
            return new LaunchScreen(new LaunchScreenModule(bridge, module), module.version);
        }
    };

    /**
     * Returns a shared instance.
     */
    static getInstance = once(() => {
        const bridge = Bridge.getInstance();
        if (bridge) {
            return LaunchScreen.create(bridge);
        }
    });

    /** Returns whether the launch screen is currently showing. */
    readonly isShowing: () => Promise<boolean>;

    /** Shows the launch screen. */
    readonly show: () => Promise<void>;

    /** Hides the launch screen. */
    readonly hide: () => Promise<void>;

    constructor(
        readonly module: LaunchScreenModule,
        readonly version: Version<0>,
    ) {
        this.isShowing = module.isShowing.bind(module);
        this.show = module.show.bind(module);
        this.hide = module.hide.bind(module);
    }
}
